/**
 * Hooks para DJ 1926 — Base Imponible 1ª Categoría + Datos Contables.
 * Anexo 15/16 de la Resolución Exenta SII N°104/2025.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Types ───────────────────────────────────────────────────────────────────

export type AjusteTipo = 'agregado' | 'deduccion'

export interface DJ1926Summary {
  year: number
  has_header: boolean
  resultado_financiero: string
  total_agregados: string
  total_deducciones: string
  deduccion_14e: string
  rli: string
  count_rli_ajustes: number
  count_patrimonio: number
}

export interface DJ1926HeaderPayload {
  folio_rli_inicio?: string
  folio_rli_final?: string
  resultado_financiero?: string | null
  deduccion_14e?: string
  notes?: string
}

export interface RLIAdjustment {
  id: string
  line_number: number
  partida_concept: string
  sii_account_code: string
  descripcion: string
  monto: string
  tipo: AjusteTipo
}

export interface RLIAdjustmentPayload {
  partida_concept: string
  sii_account_code?: string
  descripcion?: string
  monto: string
  tipo: AjusteTipo
}

export interface PatrimonyAdjustment {
  id: string
  line_number: number
  partida_code: string
  account_id: string
  account_code: string
  account_name: string
  sii_code: string
  ifrs_deudor: string
  ifrs_acreedor: string
  ejercicio_deudor: string
  ejercicio_acreedor: string
}

export interface PatrimonyAdjustmentPayload {
  account_id: string
  partida_code?: string
  ifrs_deudor?: string
  ifrs_acreedor?: string
  ejercicio_deudor?: string
  ejercicio_acreedor?: string
}

// ─── Header ──────────────────────────────────────────────────────────────────

export function useDJ1926Summary(year: number) {
  return useQuery<DJ1926Summary>({
    queryKey: ['dj1926', year, 'summary'],
    queryFn: async () => (await apiClient.get(`/accounting/dj-1926/${year}/`)).data,
  })
}

export function useSaveDJ1926Header(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: DJ1926HeaderPayload) =>
      (await apiClient.put(`/accounting/dj-1926/${year}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dj1926', year] })
    },
  })
}

// ─── Ajustes RLI ─────────────────────────────────────────────────────────────

export function useRLIAdjustments(year: number) {
  return useQuery<{ items: RLIAdjustment[]; count: number }>({
    queryKey: ['dj1926', year, 'rli'],
    queryFn: async () => (await apiClient.get(`/accounting/dj-1926/${year}/rli/`)).data,
  })
}

export function useCreateRLIAdjustment(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: RLIAdjustmentPayload) =>
      (await apiClient.post(`/accounting/dj-1926/${year}/rli/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dj1926', year] })
    },
  })
}

export function useDeleteRLIAdjustment(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.delete(`/accounting/dj-1926/rli/${id}/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dj1926', year] })
    },
  })
}

// ─── Ajustes Patrimonio ──────────────────────────────────────────────────────

export function usePatrimonyAdjustments(year: number) {
  return useQuery<{ items: PatrimonyAdjustment[]; count: number }>({
    queryKey: ['dj1926', year, 'patrimonio'],
    queryFn: async () =>
      (await apiClient.get(`/accounting/dj-1926/${year}/patrimonio/`)).data,
  })
}

export function useCreatePatrimonyAdjustment(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PatrimonyAdjustmentPayload) =>
      (await apiClient.post(`/accounting/dj-1926/${year}/patrimonio/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dj1926', year] })
    },
  })
}

export function useDeletePatrimonyAdjustment(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.delete(`/accounting/dj-1926/patrimonio/${id}/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dj1926', year] })
    },
  })
}
